import React, { useState } from 'react';
import Navigation from '../components/Navigation.jsx';
import Footer from '../components/Footer.jsx';

function WorkbookComingSoon() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // For now, just show success message
    // You can later integrate with your email service
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream-50 to-white">
      <Navigation isDarkBg={false} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Coming Soon Badge */}
          <div className="inline-block px-5 py-2.5 glass-warm rounded-full mb-8 animate-float">
            <span className="text-warm-700 text-sm font-semibold tracking-wide">
              🔜 Coming Soon
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-sage-800 mb-6 leading-tight">
            Boundary Reset Workbook
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed mb-8 max-w-3xl mx-auto">
            Stop Overcommitting. Start Feeling Confident.
          </p>

          {/* Description */}
          <p className="text-lg text-gray-700 leading-relaxed mb-12 max-w-2xl mx-auto">
            A guided workbook with practical tools to identify weak boundaries, overcome feelings, and assess your progress. Get lifetime access with updates as soon as it's ready.
          </p>

          {/* Image Preview */}
          <div className="mb-12 rounded-2xl overflow-hidden shadow-2xl max-w-2xl mx-auto">
            <img 
              src="https://images.squarespace-cdn.com/content/v1/5e7be0f4a4a1a06a00b2ccbf/25730e1c-0fbf-4d2a-b168-ef8f151812d5/Original+CCM+Workbook+Template.png"
              alt="Boundary Reset Workbook Preview"
              className="w-full h-auto"
            />
          </div>

          {/* Email Signup Form */}
          <div className="max-w-xl mx-auto">
            {!submitted ? (
              <div className="glass-light rounded-2xl p-8 shadow-xl border border-white/30">
                <h3 className="text-2xl font-serif font-semibold text-sage-800 mb-4">
                  Get Notified When It Launches
                </h3>
                <p className="text-gray-600 mb-6">
                  Be the first to know when the Boundary Reset Workbook is available. 
                  Plus, get early-bird pricing exclusive to our waitlist.
                </p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    required
                    className="w-full px-6 py-4 rounded-full border-2 border-sage-200 focus:border-warm-500 focus:outline-none transition-colors text-lg"
                  />
                  <button
                    type="submit"
                    className="w-full px-8 py-4 bg-warm-600 text-white rounded-full text-lg font-bold hover:bg-warm-700 transition-all duration-300 hover:shadow-xl hover:scale-105 transform"
                  >
                    Join the Waitlist
                  </button>
                </form>
                <p className="text-sm text-gray-500 mt-4">
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </div>
            ) : (
              <div className="glass-light rounded-2xl p-8 shadow-xl border border-white/30 bg-sage-50/50">
                <div className="text-center">
                  <div className="inline-block p-4 bg-sage-100 rounded-full mb-4">
                    <svg className="w-12 h-12 text-sage-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-serif font-semibold text-sage-800 mb-3">
                    You're on the list!
                  </h3>
                  <p className="text-gray-600 text-lg">
                    We'll send you an email as soon as the Boundary Reset Workbook is ready, 
                    along with your exclusive early-bird discount.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* What's Inside */}
          <div className="mt-16 max-w-3xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-serif font-semibold text-sage-800 mb-8">
              What's Inside the Workbook
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="glass-light rounded-xl p-6 text-left">
                <div className="w-12 h-12 bg-warm-100 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl">✍️</span>
                </div>
                <h4 className="font-semibold text-sage-800 mb-2">Self-Paced Exercises</h4>
                <p className="text-gray-600">Practical exercises and reflections you can do at your own pace.</p>
              </div>
              <div className="glass-light rounded-xl p-6 text-left">
                <div className="w-12 h-12 bg-warm-100 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl">🎯</span>
                </div>
                <h4 className="font-semibold text-sage-800 mb-2">Identify Weak Boundaries</h4>
                <p className="text-gray-600">Learn to spot the patterns that keep you overcommitting.</p>
              </div>
              <div className="glass-light rounded-xl p-6 text-left">
                <div className="w-12 h-12 bg-warm-100 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl">📊</span>
                </div>
                <h4 className="font-semibold text-sage-800 mb-2">Track Your Progress</h4>
                <p className="text-gray-600">Assess your growth and celebrate your wins along the way.</p>
              </div>
              <div className="glass-light rounded-xl p-6 text-left">
                <div className="w-12 h-12 bg-warm-100 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl">♾️</span>
                </div>
                <h4 className="font-semibold text-sage-800 mb-2">Lifetime Access</h4>
                <p className="text-gray-600">Get all future updates and improvements at no extra cost.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default WorkbookComingSoon;
